import { type ComponentProps, useCallback, useMemo } from 'react';
import { Typography } from '@forethought-technologies/forethought-elements';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  quickFeedbackClickedMobile,
  quickFeedbackSent,
  selectFeatureFlags,
  selectQuickFeedbackConfig,
  selectQuickFeedbackSteps,
  selectWidgetComponents,
  setLatestStepRerouted,
} from 'reducers/workflows';
import { isArticleComponent, isTextMessageComponent } from 'api/types';
import { sendConversationFeedback } from 'actions/workflows-v2/workflowsV2';
import AdditionalFeedbackModal from './AdditionalFeedbackModal';
import { selectIsMobile } from 'reducers/main';
import MobileQuickFeedbackModal from './MobileQuickFeedbackModal';
import QuickFeedbackButtons from './QuickFeedbackButtons';
import { scrollToTheBottom } from 'utils/helpers';

export function QuickFeedback({
  stepIndex,
}: {
  stepIndex: number;
}): JSX.Element {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const quickFeedbackConfig = useAppSelector(selectQuickFeedbackConfig);
  const quickFeedbackSteps = useAppSelector(selectQuickFeedbackSteps);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const widgetComponents = useAppSelector(selectWidgetComponents);
  const isMobile = useAppSelector(selectIsMobile);
  const isQuickFeedbackEnabled = featureFlags.includes('quick_feedback');

  const quickFeedbackStep = quickFeedbackSteps[stepIndex];

  const widgetComponent = widgetComponents[stepIndex];

  const send = useCallback(
    /**
     * @param isUseful - If passed, then we're making a request right away.
     * If not, then we're sending additional feedback from the modal window.
     */
    (isUseful?: boolean) => {
      const currentQuickFeedbackStep =
        isUseful === undefined
          ? quickFeedbackStep
          : {
              additionalFeedback: '',
              isSent: false,
              isUseful,
            };

      if (!currentQuickFeedbackStep) {
        return;
      }

      if (!widgetComponent) {
        return;
      }

      const isArticle =
        isArticleComponent(widgetComponent) ||
        isTextMessageComponent(widgetComponent);

      if (!isArticle) {
        return;
      }

      const { knowledge_articles: knowledgeArticles } =
        widgetComponent.component_fields;

      if (!knowledgeArticles) {
        return;
      }

      if (
        !currentQuickFeedbackStep.isUseful &&
        quickFeedbackConfig?.negative_routing_intent_workflow_id
      ) {
        dispatch(setLatestStepRerouted());
        scrollToTheBottom('smooth');
      }

      dispatch(
        sendConversationFeedback({
          feedbackFields: {
            additionalFeedback: currentQuickFeedbackStep.additionalFeedback,
            articleStepData: widgetComponent.component_fields.article_step_data,
            isUseful: currentQuickFeedbackStep.isUseful,
            knowledgeArticles,
            textMessageOverride: isArticleComponent(widgetComponent)
              ? widgetComponent.component_fields.text_message_override
              : undefined,
          },
        }),
      );

      dispatch(quickFeedbackSent(stepIndex));
    },
    [
      dispatch,
      quickFeedbackConfig?.negative_routing_intent_workflow_id,
      quickFeedbackStep,
      stepIndex,
      widgetComponent,
    ],
  );

  const handleMobileClick = useCallback(() => {
    dispatch(quickFeedbackClickedMobile(stepIndex));
  }, [dispatch, stepIndex]);

  const isRelevantWidgetComponent =
    widgetComponent &&
    (isArticleComponent(widgetComponent) ||
      (isTextMessageComponent(widgetComponent) &&
        widgetComponent.component_fields.knowledge_articles &&
        widgetComponent.component_fields.knowledge_articles.length > 0));

  const isTextMessage = useMemo(() => {
    if (!widgetComponent) {
      return false;
    }

    return (
      isTextMessageComponent(widgetComponent) ||
      (isArticleComponent(widgetComponent) &&
        (widgetComponent.component_fields.text_message_override ||
          widgetComponent.component_type === 'article'))
    );
  }, [widgetComponent]);

  if (
    !isQuickFeedbackEnabled ||
    !quickFeedbackConfig ||
    !quickFeedbackConfig.is_enabled ||
    !isRelevantWidgetComponent
  ) {
    return <></>;
  }

  const wrapperProps = isMobile
    ? ({
        bgcolor: 'transparent',
        border: 'none',
        component: 'button',
        onClick: handleMobileClick,
      } as const satisfies ComponentProps<typeof Box<'button'>>)
    : ({
        component: 'form',
        title: quickFeedbackConfig.feedback_prompt,
      } as const satisfies ComponentProps<typeof Box<'form'>>);

  return (
    <>
      <Box
        alignItems='center'
        display='flex'
        gap='7.5px'
        marginTop='-10px'
        paddingBottom='24px'
        paddingLeft={isTextMessage ? '24px' : '10px'}
        paddingTop='8px'
        sx={{
          '& ~ .user-message': {
            marginTop: '-10px',
          },
          cursor: wrapperProps.component === 'button' ? 'pointer' : 'default',
        }}
        width='fit-content'
        {...wrapperProps}
      >
        {quickFeedbackConfig.feedback_prompt && (
          <Typography color={theme.palette.colors.grey[700]} variant='font14'>
            {quickFeedbackConfig.feedback_prompt}
          </Typography>
        )}
        <QuickFeedbackButtons
          isButton={!isMobile}
          send={send}
          size={24}
          stepIndex={stepIndex}
        />
      </Box>
      <MobileQuickFeedbackModal stepIndex={stepIndex}>
        <Box
          component='form'
          display='flex'
          gap='28px'
          title={quickFeedbackConfig.feedback_prompt}
        >
          <QuickFeedbackButtons
            isButton
            send={send}
            size={39}
            stepIndex={stepIndex}
          />
        </Box>
      </MobileQuickFeedbackModal>
      <AdditionalFeedbackModal send={send} stepIndex={stepIndex} />
    </>
  );
}
